import { http, metadata } from 'lib_ui-services';

const _p = {
    http,
    getNamespaces: metadata.getNamespaces
};

export const _private = _p;

export default {
    verb: 'doingGet',
    namespace: 'application',
    relation: 'swagger',
    logic: doingGet
};

async function doingGet({ context }) {
    const { session } = context;
    const useCaseId = session?.useCase['metaui:useCase']._id;
    const groupNumber = session?.groupNumber ?? 0;

    const openApiDoc = await tryGetOpenApi({ useCaseId, groupNumber });
    if (openApiDoc) {
        const namespaces = _p.getNamespaces();
        const customApis = namespaces.filter(ns => ns.withCustomApi && !ns.hideApiDocs).map(ns => ns.title);
        if (customApis.length > 0) {
            const customSwaggers = await Promise.all(
                customApis.map(async namespaceTitle => {
                    try {
                        return await _p.http.get(`/docs/${namespaceTitle}/swagger`, true);
                    } catch {
                        return null;
                    }
                })
            );
            if (customSwaggers.some(swagger => swagger != null)) {
                const mergedOpenApi = merge(openApiDoc, namespaces, customSwaggers, customApis);
                return { result: { swagger: mergedOpenApi } };
            }
        }
        return { result: { swagger: openApiDoc } };
    }

    // no need to add the group. http will add the group for us.
    const useCaseSwagger = await _p.http.get(`/api/metaui/swagger/${useCaseId}`, true);
    const namespaces = _p.getNamespaces();
    const customApis = namespaces.filter(ns => ns.withCustomApi && !ns.hideApiDocs).map(ns => ns.title);
    const customSwaggers = await Promise.all(
        customApis.map(namespaceTitle => _p.http.get(`/docs/${namespaceTitle}/swagger`, true))
    );
    const combinedSwagger = merge(useCaseSwagger, namespaces, customSwaggers, customApis);
    combinedSwagger.basePath = `/g/${groupNumber}${combinedSwagger.basePath}`;
    return { result: { swagger: combinedSwagger } };
}

export function merge(useCaseSwagger, namespaces, customSwaggers, customApis) {
    const isOpenApi = typeof useCaseSwagger.openapi === 'string' && useCaseSwagger.openapi.startsWith('3.1');
    let mainApi = { ...useCaseSwagger };

    // Ensure components.schemas exists for OpenAPI 3.1
    if (isOpenApi) {
        if (!mainApi.components) {
            mainApi.components = {};
        }
        if (!mainApi.components.schemas) {
            mainApi.components.schemas = {};
        }
    }

    for (let i = 0; i < customSwaggers.length; i++) {
        if (!customSwaggers[i]) continue;

        const namespaceTitle = customApis[i];
        const namespace = namespaces.find(ns => ns.title === namespaceTitle);
        const prettyName = namespace?.prettyName || namespaceTitle;

        // Merge all custom APIs endpoints into one tag
        if (customSwaggers[i].paths) {
            Object.values(customSwaggers[i].paths).forEach(path => {
                Object.values(path).forEach(operation => {
                    if (operation && typeof operation === 'object') {
                        operation.tags = [prettyName];
                        if (operation.responses && operation.responses.default) {
                            delete operation.responses.default;
                        }
                    }
                });
            });
            Object.keys(customSwaggers[i].paths).forEach(path => {
                customSwaggers[i].paths[`/${namespaceTitle}${path}`] = customSwaggers[i].paths[path];
                delete customSwaggers[i].paths[path];
            });
        }

        mainApi.paths = { ...mainApi.paths, ...customSwaggers[i].paths };

        // Handle definitions (Swagger v2) vs components.schemas (OpenAPI 3.1)
        if (customSwaggers[i].definitions) {
            if (isOpenApi) {
                Object.keys(customSwaggers[i].definitions).forEach(defKey => {
                    mainApi.components.schemas[defKey] = customSwaggers[i].definitions[defKey];
                });
            } else {
                mainApi.definitions = { ...mainApi.definitions, ...customSwaggers[i].definitions };
            }
        }

        if (!mainApi.tags) {
            mainApi.tags = [];
        }
        const existingTag = mainApi.tags.find(tag => tag.name === prettyName);
        if (!existingTag) {
            mainApi.tags.push({ name: prettyName, description: '' });
        }
    }

    if (mainApi.tags) {
        mainApi.tags.sort((a, b) => {
            //Security comes first
            if (a.name === 'Security') return -1;
            if (b.name === 'Security') return 1;
            //Item comes second
            if (a.name === 'Item') return -1;
            if (b.name === 'Item') return 1;
            //location comes third
            if (a.name === 'Location') return -1;
            if (b.name === 'Location') return 1;
            //everything else comes alphabetically
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
        });
    }
    return mainApi;
}

async function tryGetOpenApi({ useCaseId, groupNumber }) {
    try {
        const openApi = await _p.http.get(`/api/metaui/openapi/${useCaseId}`, true);
        if (!isValidOpenApi(openApi)) return;
        return normalizeServers(openApi, groupNumber);
    } catch {
        return;
    }
}

function isValidOpenApi(openApi) {
    if (openApi == null || typeof openApi !== 'object') return false;
    if (typeof openApi.openapi !== 'string' || !openApi.openapi.startsWith('3.1')) return false;
    if (!openApi.paths || typeof openApi.paths !== 'object') return false;
    return true;
}

function normalizeServers(openApi, groupNumber) {
    const servers = Array.isArray(openApi.servers) && openApi.servers.length > 0 ? openApi.servers : [{ url: '/api' }];
    const normalizedServers = servers.map(server => {
        const url = server?.url || '';
        if (url.startsWith('/') && !url.startsWith('/g/')) {
            return { ...server, url: `/g/${groupNumber}${url}` };
        }
        return server;
    });
    return { ...openApi, servers: normalizedServers };
}
